import { Fragment, useMemo, useState } from 'react';
import { NJBadge, NJButton, NJInputSearch, NJIconButton } from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import {
    Size2MixAppShell,
    Size2MixNetworkIcon,
    Size2MixPageHeader,
    PUC_EDM_NETWORK,
    useSize2MixRefNavigation,
    type ContractListItem,
} from '@shared/size2mix-shell';
import { useContracts } from './hooks';

export const meta = {
    title: 'Référence — Contrats',
    description: 'Page de référence « Tous les contrats » : compteurs, recherche et tableau à lignes dépliables (réseaux rattachés au contrat).',
    tags: ['contract', 'list'],
    author: 'dv5886',
    status: 'review' as const,
    category: 'hors-reseau/contracts',
    createdAt: '2026-10-10',
};

const COLUMNS = ['', "Phase d'étude", 'Statut', 'Nom du réseau', 'Description', 'Propriétaire', 'Modifié le', 'Actions'];

const cell: React.CSSProperties = {
    padding: '10px 12px',
    borderBottom: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
    textAlign: 'left',
    verticalAlign: 'middle',
};

export default function RefContracts() {
    const nav = useSize2MixRefNavigation();
    const { data } = useContracts();
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    const rows = useMemo(() => {
        const q = search.toLowerCase();
        return (data?.items ?? []).filter((c) => `${c.code} ${c.name}`.toLowerCase().includes(q));
    }, [data, search]);

    const toggle = (id: string) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const renderContract = (c: ContractListItem) => {
        const isOpen = expanded.has(c.id);
        return (
            <Fragment key={c.id}>
                <tr style={{ background: 'var(--nj-semantic-color-background-neutral-secondary-default)' }}>
                    <td colSpan={COLUMNS.length} style={{ ...cell, padding: '0 20px 0 64px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', height: 47, fontFamily: 'system-ui', gap: 4 }}>
                            <NJIconButton icon={isOpen ? 'expand_less' : 'expand_more'} label={`${isOpen ? 'Replier' : 'Déplier'} ${c.code || c.name}`} variant="tertiary" scale="xs" onClick={() => toggle(c.id)} />
                            <span style={{ fontSize: 14, marginRight: 8, color: 'var(--nj-core-color-grey-600)' }}>{c.code}</span>
                            <span style={{ fontSize: 11, fontWeight: 700 }}>{c.name}</span>
                            <span style={{ marginLeft: 'auto' }}><NJIconButton icon="edit" label={`Modifier le contrat ${c.code || c.name}`} variant="tertiary" scale="xs" /></span>
                        </div>
                    </td>
                </tr>
                {isOpen && c.networks.length === 0 && <tr><td colSpan={COLUMNS.length} style={{ ...cell, paddingLeft: 92 }}>Les réseaux rattachés à ce contrat ne sont pas inclus dans la capture hors ligne.</td></tr>}
                {isOpen &&
                    c.networks.map((name) => (
                        <tr key={name}>
                            <td style={cell} />
                            <td style={cell} colSpan={2} />
                            <td style={cell}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                    <Size2MixNetworkIcon fluidType={c.fluidType} size={18} />
                                    <a
                                        href="#"
                                        className="nj-link nj-link--sm"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            if (name === PUC_EDM_NETWORK.name) nav.goTo('general');
                                        }}
                                    >
                                        {name}
                                    </a>
                                </span>
                            </td>
                            <td style={cell}>-</td>
                            <td style={cell}>{c.owner}</td>
                            <td style={cell}>{c.updatedAt}</td>
                            <td style={cell}>
                                <NJIconButton icon="link_off" label="Détacher du contrat" variant="tertiary" scale="sm" />
                            </td>
                        </tr>
                    ))}
            </Fragment>
        );
    };

    return (
        <PrototypeStudioLayout title={meta.title}>
            <Size2MixAppShell
                activePrimary="contracts"
                onPrimaryNavigate={nav.onPrimaryNavigate}
            >
                <Size2MixPageHeader
                    title="Tous les contrats"
                    badges={
                        <>
                            <NJBadge variant="information" emphasis="subtle">{`${data?.totalContracts ?? '…'} Contrats`}</NJBadge>
                            <NJBadge variant="neutral" emphasis="subtle">{`${data?.totalNetworks ?? '…'} Réseaux`}</NJBadge>
                        </>
                    }
                    actions={
                        <span style={{ display: 'inline-flex', gap: 8 }}>
                            <NJButton label="Nouveau réseau" />
                            <NJButton label="Nouveau contrat" />
                        </span>
                    }
                />
                <div style={{ width: 540, maxWidth: '100%', margin: '12px 0 20px' }}>
                    <NJInputSearch
                        label="Rechercher un contrat"
                        placeholder="Rechercher un contrat"
                        resetButtonTitle="Effacer la recherche"
                        scale="sm"
                        value={search}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                    />
                </div>
                <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: 1150, tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: 14 }}>
                    <colgroup>{COLUMNS.map((_, index) => <col key={index} style={{ width: index === 0 ? 85 : 150 }} />)}</colgroup>
                    <thead>
                        <tr>
                            {COLUMNS.map((c, i) => (
                                <th key={i} style={{ ...cell, height: 46, padding: '0 18px', fontFamily: 'system-ui', fontSize: 11, fontWeight: 600, color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>
                                    {c}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>{rows.map(renderContract)}</tbody>
                </table>
                </div>
            </Size2MixAppShell>
        </PrototypeStudioLayout>
    );
}
